import React, { useEffect } from 'react';
import { sortBy } from 'lodash';
import {
  Form,
  FormInstance,
  DateSerialized,
  CheckboxGroup,
  DateInput,
  TextInput,
  TextInputModes,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useScheduleForm } from '../../../../contexts/form';

interface EpxPPDepressData {
  method: string;
  language: string;
  birthDate: string;
  end?: string;
  tz: string;
  times: string[];
  deltas: number[];
  isNICU: boolean;
  includeEducation: boolean;
  isCustomSupport: boolean;
  supportName?: string;
  supportPhone?: string;
  showSupportPhone?: boolean;
}

type EpxPPDepressDataRaw = DateSerialized<
  EpxPPDepressData,
  'birthDate' | 'end'
>;

export const EpxPPDepressDefaultData: EpxPPDepressData = {
  method: 'message',
  language: 'en',
  birthDate: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['19:00'],
  deltas: [2, 4, 8, 16, 24],
  isNICU: false,
  includeEducation: true,
  isCustomSupport: false,
  supportName: null,
  supportPhone: null,
  showSupportPhone: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxPPDepressFormProps {
  setForm?: (form: FormInstance<EpxPPDepressDataRaw>) => void;
  onSubmit?: (data: EpxPPDepressData) => void;
  isDisabled?: boolean;
}

const EpxPPDepressForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxPPDepressFormProps): JSX.Element => {
  const { isEditMode } = useScheduleForm();
  const serialize = (data: EpxPPDepressDataRaw): EpxPPDepressData => {
    let { supportPhone, supportName } = data;
    if (!data.isCustomSupport) {
      supportPhone = null;
      supportName = null;
    } else {
      if (!(data.supportPhone && data.supportPhone.trim() !== '')) {
        supportPhone = null;
      }
      if (!(data.supportName && data.supportName.trim() !== '')) {
        supportName = null;
      }
    }
    return {
      ...data,
      birthDate: SerializeDate(data.birthDate, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1),
      supportPhone,
      supportName,
      deltas: sortBy(data.deltas)
    };
  };
  const deserialize = (data: EpxPPDepressData): EpxPPDepressDataRaw => {
    let isCustomSupport = data.isCustomSupport;
    if (isCustomSupport == null) {
      if (data.supportPhone && data.supportPhone.trim() !== '') {
        isCustomSupport = true;
      } else {
        isCustomSupport = false;
      }
    }
    return {
      ...data,
      method: data.method || 'message',
      birthDate: DeserializeDate(data.birthDate, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      isCustomSupport
    };
  };
  const defaultData = deserialize(EpxPPDepressDefaultData);
  const form = new FormInstance<EpxPPDepressDataRaw, EpxPPDepressData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxPPDepressData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod isMessageOnly />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <DateInput
                      label="Child Birth Date"
                      name="birthDate"
                      required
                      fill
                      disabled={isEditMode}
                    />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Program Type"
                      name="isNICU"
                      trueLabel="Neonatal Intensive Care Unit (NICU)"
                      falseLabel="Default"
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Educational & Motiavational Messages"
                      name="includeEducation"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <CheckboxGroup
                      label="Surveys At"
                      name="deltas"
                      options={[
                        {
                          label: '2 Weeks Post Birth',
                          value: 2
                        },
                        {
                          label: '4 Weeks Post Birth',
                          value: 4
                        },
                        {
                          label: '2 Months Post Birth',
                          value: 8
                        },
                        {
                          label: '4 Months Post Birth',
                          value: 16
                        },
                        {
                          label: '6 Months Post Birth',
                          value: 24
                        }
                      ]}
                      minItems={1}
                      inline
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Use Custom Support Line"
                      name="isCustomSupport"
                      trueLabel="Yes"
                      falseLabel="No (Use National Hotline)"
                      fill
                    />
                  </Col>
                  {props.values.isCustomSupport ? (
                    <Col sm={12}>
                      <TextInput
                        label="Custom Support Phone"
                        name="supportPhone"
                        mode={TextInputModes.PhoneStrict}
                        required
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
                {props.values.isCustomSupport ? (
                  <Row>
                    <Col sm={12}>
                      <SelectBooleanInput
                        label="Show Support Phone"
                        name="showSupport Phone"
                        trueLabel="Yes (Mentioned in Messages)"
                        falseLabel="No (Hide Number from Messages)"
                        fill
                      />
                    </Col>
                    <Col sm={12}>
                      <TextInput
                        label="Support Phone Name"
                        name="supportName"
                        fill
                      />
                    </Col>
                  </Row>
                ) : null}
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxPPDepressForm;
